import { useMemo, useState } from "react";
import { Alert, Button, Col, Dropdown, Row, Spinner, Table } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useGetAllBookings } from "../../../../api/fleet-management/bookings/booking.hooks";
import {
  formatBookingAmount,
  formatBookingDateTime,
  normalizeBookings,
} from "./utils/booking.utils";
import BookingDriverExpensesSection from "./_components/BookingDriverExpensesSection";
import BookingExpensesSection from "./_components/BookingExpensesSection";
import CancelBookingModal from "./_components/CancelBookingModal";
import BookingVoucherPrintButton from "./_components/BookingVoucherPrintButton";
import ConfirmBookingModal from "./_components/ConfirmBookingModal";
import CompleteBookingModal from "./_components/CompleteBookingModal";
import BookingDetailsPaymentsSection from "./_components/BookingDetailsPaymentsSection";
import BookingVehicleInformationSection from "./_components/BookingVehicleInformationSection";
import DriverAssignmentsTable from "./_components/DriverAssignmentsTable";

const renderValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const BookingDetailsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { bookingId } = useParams();
  const { data, isLoading, isError, error } = useGetAllBookings();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const bookingFromState = location?.state?.booking;
  const bookings = useMemo(() => normalizeBookings(data), [data]);

  const booking = useMemo(() => {
    return bookings.find((item) => item?._id === bookingId) || bookingFromState || null;
  }, [bookingFromState, bookingId, bookings]);

  const customerName = booking?.customerSnapshot?.name || booking?.fullName;
  const companyName = booking?.customerSnapshot?.companyName || booking?.companyName;
  const phoneNumber = booking?.customerSnapshot?.phoneNumber || booking?.phoneNumber;
  const emergencyContactNumber = booking?.emergencyContactNumber;
  const email = booking?.customerSnapshot?.email || booking?.email;

  const vehicles = Array.isArray(booking?.vehicles) ? booking.vehicles : [];
  const driverAssignments = Array.isArray(booking?.driverAssignments) ? booking.driverAssignments : [];

  const assignedDrivers = useMemo(() => {
    return driverAssignments
      .map((assignment) => {
        const driver = assignment?.driverSnapshot || assignment?.driverId || null;
        const driverId = getIdValue(assignment?.driverId);
        const vehicleId = getIdValue(assignment?.vehicleId);
        const vehicle = vehicles.find((item) => getIdValue(item?.vehicleId) === vehicleId);
        const vehicleLabel =
          vehicle?.snapshot?.name ||
          vehicle?.vehicleId?.name ||
          "Vehicle";

        if (!driverId) return null;

        return {
          driverId,
          driverName: driver?.name || "Assigned driver",
          vehicleLabel,
        };
      })
      .filter(Boolean)
      .filter((item, index, array) => array.findIndex((entry) => entry.driverId === item.driverId) === index);
  }, [driverAssignments, vehicles]);

  if (!booking && isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking && isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body py-4">
            <Alert variant="danger" className="mb-0">
              Failed to load booking details. {error?.response?.data?.message || "Please try again."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-center py-5">
            <h5 className="mb-2">Booking not found</h5>
            <p className="text-muted mb-3">This booking may have been deleted or the link is invalid.</p>
            <Button variant="primary" onClick={() => history.push("/fleet-management/bookings")}>Back to Bookings</Button>
          </div>
        </div>
      </div>
    );
  }

  const assignmentsByVehicleId = driverAssignments.reduce((acc, assignment) => {
    const key = getIdValue(assignment?.vehicleId);
    if (!key) return acc;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(assignment);
    return acc;
  }, {});

  const refundDetails = booking?.refundDetails || null;
  const isCancelled = booking?.status === "cancelled";
  const isCompleted = booking?.status === "completed";
  const canConfirm = booking?.status === "pending";
  const canComplete = booking?.status === "confirmed";
  const canCancel = !isCancelled && !isCompleted;
  const displayBookingId = booking?.bookingId || booking?._id;

  const openCancelModal = () => {
    setFeedback({ type: "", message: "" });
    setShowCancelModal(true);
  };

  const openConfirmModal = () => {
    setFeedback({ type: "", message: "" });
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const openCompleteModal = () => {
    setFeedback({ type: "", message: "" });
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Booking Details</h4>
        <div className="d-flex gap-2">
          <Dropdown align="end">
            <Dropdown.Toggle variant="primary">Actions</Dropdown.Toggle>
            <Dropdown.Menu>
              {canConfirm ? <Dropdown.Item onClick={openConfirmModal}>Confirm Booking</Dropdown.Item> : null}
              {canComplete ? <Dropdown.Item onClick={openCompleteModal}>Complete Booking</Dropdown.Item> : null}
              {canCancel ? (
                <Dropdown.Item onClick={openCancelModal} className="text-danger">
                  Cancel Booking
                </Dropdown.Item>
              ) : null}
              {!canConfirm && !canComplete && !canCancel ? (
                <Dropdown.Item disabled>No actions available</Dropdown.Item>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="light" onClick={() => history.goBack()}>
            Back
          </Button>
        </div>
      </div>

      {feedback.message ? (
        <Alert variant={feedback.type} className="mb-3">
          {feedback.message}
        </Alert>
      ) : null}

      <div className="card mb-3">
        <div className="card-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="text-muted mb-1">Booking ID</div>
              <div className="fw-semibold">{renderValue(displayBookingId)}</div>
            </Col>
            <Col md={3}>
              <div className="text-muted mb-1">Status</div>
              {/* <span className={getBookingStatusBadgeClass(booking?.status)}> */}
                {(booking?.status || "unknown").replace("_", " ")}
              {/* </span> */}
            </Col>
            <Col md={3}>
              <div className="text-muted mb-1">Total Amount</div>
              {/* <Badge bg="light" text="dark" className="fs-6"> */}
                {formatBookingAmount(booking?.totalAmount)}
              {/* </Badge> */}
            </Col>
          </Row>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={12}>
          <div className="card h-100">
            <div className="card-header border-0 pb-0">
              <h5 className="mb-0">Customer Information</h5>
            </div>
            <div className="card-body pt-3">
              <Row className="g-3">
                <Col sm={6}>
                  <div className="text-muted small">Full Name</div>
                  <div className="fw-semibold">{renderValue(customerName)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Company</div>
                  <div className="fw-semibold">{renderValue(companyName)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Phone Number</div>
                  <div className="fw-semibold">{renderValue(phoneNumber)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Emergency Contact Number</div>
                  <div className="fw-semibold">{renderValue(emergencyContactNumber)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Email</div>
                  <div className="fw-semibold">{renderValue(email)}</div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        <BookingVehicleInformationSection bookingId={booking?._id} vehicles={vehicles} />

        <Col lg={6}>
          <div className="card h-100">
            <div className="card-header border-0 pb-0">
              <h5 className="mb-0">Trip Information</h5>
            </div>
            <div className="card-body pt-3">
              <Row className="g-3">
                <Col sm={6}>
                  <div className="text-muted small">Pickup Location</div>
                  <div className="fw-semibold">{renderValue(booking?.pickupLocation)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Dropoff Location</div>
                  <div className="fw-semibold">{renderValue(booking?.dropoffLocation)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Pickup Date & Time</div>
                  <div className="fw-semibold">{formatBookingDateTime(booking?.pickupDateTime)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Dropoff Date & Time</div>
                  <div className="fw-semibold">{formatBookingDateTime(booking?.dropoffDateTime)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Created At</div>
                  <div className="fw-semibold">{formatBookingDateTime(booking?.createdAt)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Last Updated</div>
                  <div className="fw-semibold">{formatBookingDateTime(booking?.updatedAt)}</div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        {/* <Col lg={6}>
          <div className="card h-100">
            <div className="card-header border-0 pb-0">
              <h5 className="mb-0">Refund Details</h5>
            </div>
            <div className="card-body pt-3">
              {refundDetails ? (
                <Row className="g-3">
                  <Col sm={6}>
                    <div className="text-muted small">Refunded</div>
                    <div className="fw-semibold">{refundDetails?.isRefunded ? "Yes" : "No"}</div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted small">Refund Type</div>
                    <div className="fw-semibold text-capitalize">
                      {renderValue(refundDetails?.refundType)?.replace("_", " ")}
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted small">Refund Amount</div>
                    <div className="fw-semibold">
                      {refundDetails?.refundAmount === null || refundDetails?.refundAmount === undefined
                        ? "-"
                        : formatBookingAmount(refundDetails?.refundAmount)}
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted small">Refund Date</div>
                    <div className="fw-semibold">
                      {formatBookingDateTime(refundDetails?.refundDate)}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="text-muted small">Refund Reason</div>
                    <div className="fw-semibold">{renderValue(refundDetails?.refundReason)}</div>
                  </Col>
                </Row>
              ) : (
                <div className="text-muted">No refund details available.</div>
              )}
            </div>
          </div>
        </Col> */}

        <BookingDetailsPaymentsSection bookingId={booking?._id} />

        <Col lg={12}>
          <DriverAssignmentsTable vehicles={vehicles} assignmentsByVehicleId={assignmentsByVehicleId} booking={booking} />
        </Col>

        <Col lg={12}>
          <BookingExpensesSection bookingId={booking?._id} vehicles={vehicles} />
        </Col>

        <Col lg={12}>
          {assignedDrivers.length === 0 ? (
            <div className="card">
              <div className="card-body text-muted">No assigned drivers available for expense tracking.</div>
            </div>
          ) : (
            <div className="d-grid gap-3">
              {assignedDrivers.map((driver) => (
                <BookingDriverExpensesSection
                  key={driver.driverId}
                  driverId={driver.driverId}
                  driverName={driver.driverName}
                  vehicleLabel={driver.vehicleLabel}
                />
              ))}
            </div>
          )}
        </Col>
      </Row>

      <CancelBookingModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        bookingId={booking?._id}
        onSuccess={() => {
          setFeedback({ type: "success", message: "Booking cancelled successfully." });
        }}
      />

      <ConfirmBookingModal
        key={booking?._id || "confirm-booking-modal"}
        show={showConfirmModal}
        onClose={closeConfirmModal}
        booking={booking}
        onSuccess={(message) => {
          setFeedback({ type: "success", message: message || "Booking confirmed successfully." });
        }}
        onError={(message) => {
          setFeedback({ type: "danger", message: message || "Failed to confirm booking." });
        }}
      />

      <CompleteBookingModal
        show={showCompleteModal}
        booking={booking}
        onClose={closeCompleteModal}
        onSuccess={(message) => {
          setFeedback({ type: "success", message: message || "Booking completed successfully." });
        }}
        onError={(message) => {
          setFeedback({ type: "danger", message: message || "Failed to complete booking." });
        }}
      />
    </div>
  );
};

export default BookingDetailsPage;
